import { Link } from "@tanstack/react-router";
import type {
  ProgressionCriterion,
  ProgressionWizardResponse,
} from "@rua-watch/shared";
import { useDomainProgression } from "../api/hooks";

// F7 — DMARC progression wizard. Card on /domains/:domain that
// answers "can I advance the published policy to the next step?"
// Three visual states driven by the response:
//   - dataReady=false → gray "still collecting"
//   - ready=true       → green "ready, here's the DNS hint"
//   - else             → amber "almost ready" + failing criteria

export function ProgressionCard({ domain }: { domain: string }) {
  const q = useDomainProgression(domain);
  if (q.isLoading || !q.data) return null;
  const data = q.data;

  // Terminal states: no data, or already at p=reject. Render a single
  // muted card with the recommendation message and stop.
  if (data.targetPolicy == null) {
    return (
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
        <h2 className="text-sm font-medium text-slate-700 dark:text-slate-300">
          DMARC progression
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{data.recommendation}</p>
      </div>
    );
  }

  const tone = !data.dataReady ? "neutral" : data.ready ? "ready" : "almost";
  const card = TONE[tone];

  return (
    <div className={`rounded-lg border ${card.border} ${card.bg} p-4`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className={`text-xs font-medium uppercase tracking-wide ${card.label}`}>
            DMARC progression · target p={data.targetPolicy}
          </div>
          <h2 className={`mt-1 text-base font-semibold ${card.heading}`}>
            {data.recommendation}
          </h2>
        </div>
        <PolicyArrow current={data.currentPolicy} target={data.targetPolicy} tone={tone} />
      </div>

      {!data.dataReady ? (
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
          Need at least 14 days of reports to evaluate. Currently {data.dataAgeDays} day(s) of
          history collected.
        </p>
      ) : (
        <CriteriaGrid criteria={data.criteria} domain={domain} ready={data.ready} />
      )}

      {data.dnsHint ? (
        <div className="mt-4 rounded-md border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/30 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-emerald-800 dark:text-emerald-300">
            Next step
          </div>
          <p className="mt-1 text-sm text-emerald-900 dark:text-emerald-200">{data.dnsHint}</p>
        </div>
      ) : null}
    </div>
  );
}

const TONE = {
  ready: {
    border: "border-emerald-200 dark:border-emerald-900/40",
    bg:     "bg-emerald-50 dark:bg-emerald-900/20",
    label:  "text-emerald-700 dark:text-emerald-300",
    heading:"text-emerald-900 dark:text-emerald-100",
  },
  almost: {
    border: "border-amber-200 dark:border-amber-900/40",
    bg:     "bg-amber-50 dark:bg-amber-900/20",
    label:  "text-amber-700 dark:text-amber-300",
    heading:"text-amber-900 dark:text-amber-100",
  },
  neutral: {
    border: "border-slate-200 dark:border-slate-800",
    bg:     "bg-white dark:bg-slate-900",
    label:  "text-slate-500 dark:text-slate-400",
    heading:"text-slate-900 dark:text-slate-100",
  },
} as const;

function CriteriaGrid({
  criteria,
  domain,
  ready,
}: {
  criteria: ProgressionCriterion[];
  domain: string;
  ready: boolean;
}) {
  return (
    <div className="mt-4 space-y-2">
      {criteria.map((c) => (
        <CriterionRow key={c.id} criterion={c} domain={domain} surfaceContext={ready ? "all" : "failing"} />
      ))}
    </div>
  );
}

function CriterionRow({
  criterion: c,
  domain,
  surfaceContext,
}: {
  criterion: ProgressionCriterion;
  domain: string;
  surfaceContext: "all" | "failing";
}) {
  // When the parent says "ready", every criterion passes — we still
  // render them so the operator sees the proof.
  void surfaceContext;
  const status = c.passed ? "pass" : "fail";
  return (
    <div className="flex items-start gap-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
      <StatusDot status={status} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
            {c.label}
          </span>
          <span className="font-mono text-xs text-slate-600 dark:text-slate-400">
            {formatValue(c.current, c.unit)} {c.comparator === "gte" ? "/" : "vs"}{" "}
            {formatValue(c.threshold, c.unit)}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
          {c.description}
        </p>
        {!c.passed ? <CriterionAction criterion={c} domain={domain} /> : null}
      </div>
    </div>
  );
}

// Per-criterion call-to-action. Shown only when the criterion is
// failing. Links to the page where the operator can investigate.
function CriterionAction({
  criterion: c,
  domain,
}: {
  criterion: ProgressionCriterion;
  domain: string;
}) {
  if (c.id === "unknown_classification_volume" || c.id === "new_ips_per_day") {
    return (
      <p className="mt-1 text-xs">
        <Link
          to="/domains/$domain"
          params={{ domain }}
          className="text-sky-700 dark:text-sky-300 hover:underline"
        >
          Review Sources →
        </Link>
      </p>
    );
  }
  if (c.id === "dkim_aligned" || c.id === "dmarc_pass") {
    return (
      <p className="mt-1 text-xs">
        <Link
          to="/validator"
          search={{ domain }}
          className="text-sky-700 dark:text-sky-300 hover:underline"
        >
          Run DNS validator →
        </Link>
      </p>
    );
  }
  return null;
}

function StatusDot({ status }: { status: "pass" | "fail" }) {
  return (
    <span
      className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${
        status === "pass"
          ? "bg-emerald-500"
          : "bg-amber-500"
      }`}
      aria-label={status}
    />
  );
}

function PolicyArrow({
  current,
  target,
  tone,
}: {
  current: ProgressionWizardResponse["currentPolicy"];
  target: ProgressionWizardResponse["targetPolicy"];
  tone: keyof typeof TONE;
}) {
  if (!current || !target) return null;
  const t = TONE[tone];
  return (
    <div className={`shrink-0 rounded-md border ${t.border} bg-white/40 dark:bg-slate-900/40 px-3 py-1.5 text-xs font-mono ${t.heading}`}>
      <span className="opacity-70">p={current}</span>
      <span className="mx-1.5 opacity-40">→</span>
      <span className="font-semibold">p={target}</span>
    </div>
  );
}

function formatValue(value: number, unit: ProgressionCriterion["unit"]): string {
  switch (unit) {
    case "rate":
    case "percent":
      return `${(value * 100).toFixed(2)}%`;
    case "count_per_day":
      return `${value.toFixed(1)}/d`;
  }
}
